import { ConditionDependency, ConditionDependencyDetail, TextToStructuredObservation } from '@/api';
import { DEGENERATIVE_CHANGE_CONDITION, DEGENERATIVE_CHANGE_CONDITIONS } from '@/shared/constant';
import { CARE_SERVICE_ORGAN_FULL_LIST } from '@/shared/guard';
import { FindingsByOrgan } from '@/shared/model';
import { getConditionTemplateName, isEmpty, isNotEmpty, trimConditionName } from '@/shared/util';

import { ComparisonOperator, MissingField } from '../model';

const toNumber = (value: unknown): null | number => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string' && isNotEmpty(value)) {
    const parsed = parseFloat(value);

    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

export const evaluateRequiredWhen = (
  requiredWhen: ConditionDependency,
  findings: TextToStructuredObservation[],
): boolean => {
  if (!requiredWhen && !findings) {
    return false;
  }

  const { condition, operator } = requiredWhen;

  if (Array.isArray(condition)) {
    return condition.every((cond) => evaluateRequiredWhen({ condition: cond, operator }, findings));
  }

  const { condition: conditionName, field, value } = condition as ConditionDependencyDetail;

  if (operator === 'not') {
    // If only conditionName is provided (no field/value), check if condition doesn't exist
    if (conditionName && !field && !value) {
      return !findings?.find((finding) => finding.conditionTemplate.condition === conditionName);
    }

    const matchingFinding = findings?.filter(
      (finding) => finding.conditionTemplate.condition === conditionName,
    );

    if (matchingFinding?.length) {
      const matchingFindingValue = matchingFinding
        ?.map((finding) => finding.renderedSummary.reportFindings?.[0])
        .filter(Boolean)
        .flatMap((reportFinding) => reportFinding?.conditionDetail)
        .find((detail) => detail?.name === field)?.value;

      return Array.isArray(matchingFindingValue)
        ? !matchingFindingValue.includes(value as string)
        : matchingFindingValue !== value;
    }

    // When using "not" operator: if no matching finding exists, the field is required.
    // This ensures fields are required until explicitly excluded by a finding.
    return true;
  }

  // Extract field value from nested findings structure (findings -> reportFindings -> conditionDetail)
  let fieldValue: unknown = null;

  (findings ?? []).forEach((finding) => {
    (finding?.renderedSummary?.reportFindings ?? []).forEach((reportFinding) => {
      const detail = reportFinding.conditionDetail.find(
        (conditionDetail) => conditionDetail?.name === field,
      );

      if (detail?.value !== undefined) {
        fieldValue = detail.value;
      }
    });
  });

  switch (operator as ComparisonOperator) {
    case '===':
      return fieldValue === value;

    case 'any':
      return Array.isArray(fieldValue) ? fieldValue.includes(value as string) : false;

    case '>': {
      const numFieldValue = toNumber(fieldValue);
      const numValue = toNumber(value);

      return numFieldValue !== null && numValue !== null && numFieldValue > numValue;
    }

    case '>=': {
      const numFieldValue = toNumber(fieldValue);
      const numValue = toNumber(value);

      return numFieldValue !== null && numValue !== null && numFieldValue >= numValue;
    }

    case '<': {
      const numFieldValue = toNumber(fieldValue);
      const numValue = toNumber(value);

      return numFieldValue !== null && numValue !== null && numFieldValue < numValue;
    }

    case '<=': {
      const numFieldValue = toNumber(fieldValue);
      const numValue = toNumber(value);

      return numFieldValue !== null && numValue !== null && numFieldValue <= numValue;
    }

    default:
      return false;
  }
};

export const evaluateConditionShowWhen = (
  showWhen: ConditionDependency,
  allValues: { [key: string]: unknown } = {},
  organFindings: TextToStructuredObservation[] = [],
): boolean => {
  if (!showWhen) {
    return true;
  }

  const { condition, operator } = showWhen;

  if (operator === 'never' && !condition) {
    return false;
  }

  if (operator === 'or' || operator === 'and') {
    if (!Array.isArray(condition)) {
      return true;
    }

    if (operator === 'or') {
      return condition.some((cond) =>
        evaluateConditionShowWhen({ condition: cond, operator: '===' }, allValues, organFindings),
      );
    }

    return condition.every((cond) =>
      evaluateConditionShowWhen({ condition: cond, operator: '===' }, allValues, organFindings),
    );
  }

  if (operator === 'not' && Array.isArray(condition)) {
    const { field } = condition[0];
    let fieldValue = allValues[field as keyof typeof allValues];

    // Fallback to organFindings if field not found in current form values
    if (fieldValue === undefined) {
      fieldValue = organFindings
        .map((finding) => finding.renderedSummary.reportFindings?.[0])
        .filter(Boolean)
        .flatMap((reportFinding) => reportFinding?.conditionDetail)
        .find((detail) => detail?.name === field)?.value;
    }

    if (isEmpty(fieldValue)) {
      return false;
    }

    return condition.every(
      (cond) =>
        !evaluateConditionShowWhen({ condition: cond, operator: '===' }, allValues, organFindings),
    );
  }

  // This is a special case for the "neither" operator, specifically for the Uterus organ at this point
  // The logic is if any of the condition value matches the fieldValue, then return false. Otherwise, return true
  if (operator === 'neither' && Array.isArray(condition)) {
    return condition.every((cond) =>
      evaluateConditionShowWhen({ condition: cond, operator: 'not' }, allValues, organFindings),
    );
  }

  const { field, value } = condition as ConditionDependencyDetail;

  let fieldValue = allValues[field as keyof typeof allValues];

  if (fieldValue === undefined) {
    fieldValue = organFindings
      .map((finding) => finding.renderedSummary.reportFindings?.[0])
      .filter(Boolean)
      .flatMap((reportFinding) => reportFinding?.conditionDetail)
      .find((detail) => detail?.name === field)?.value;
  }

  switch (operator as ComparisonOperator) {
    case '===':
      return fieldValue === value;

    case 'any':
      return Array.isArray(value) ? value.includes(fieldValue as string) : false;

    case '>': {
      const numFieldValue = toNumber(fieldValue);
      const numValue = toNumber(value);

      return numFieldValue !== null && numValue !== null && numFieldValue > numValue;
    }

    case '>=': {
      const numFieldValue = toNumber(fieldValue);
      const numValue = toNumber(value);

      return numFieldValue !== null && numValue !== null && numFieldValue >= numValue;
    }

    case '<': {
      const numFieldValue = toNumber(fieldValue);
      const numValue = toNumber(value);

      return numFieldValue !== null && numValue !== null && numFieldValue < numValue;
    }

    case '<=': {
      const numFieldValue = toNumber(fieldValue);
      const numValue = toNumber(value);

      return numFieldValue !== null && numValue !== null && numFieldValue <= numValue;
    }

    case 'not':
      return Array.isArray(fieldValue) ? !fieldValue.includes(value) : fieldValue !== value;

    default:
      return true;
  }
};

export function evaluateMissingFields(
  finding: TextToStructuredObservation,
  result: MissingField,
  key: string,
  findingIndex: number,
  organFindings: TextToStructuredObservation[] = [],
) {
  const {
    conditionTemplate: { conditionDetail },
    renderedSummary: { reportFindings },
  } = finding;

  (reportFindings ?? []).forEach((reportFinding, observationIndex) => {
    const reportDetails = reportFinding.conditionDetail;

    const requiredFields = conditionDetail?.filter((fieldTemplate) => {
      const isRequired = fieldTemplate.requiredWhen
        ? evaluateRequiredWhen(fieldTemplate.requiredWhen, [
            {
              ...finding,
              renderedSummary: { ...finding.renderedSummary, reportFindings: [reportFinding] },
            },
          ])
        : fieldTemplate.required;

      if (!isRequired) {
        return false;
      }

      return (
        !fieldTemplate.showWhen ||
        evaluateConditionShowWhen(
          fieldTemplate.showWhen,
          reportDetails.reduce((acc, curr) => ({ ...acc, [curr.name]: curr.value }), {}),
          organFindings,
        )
      );
    });

    const missingFields = requiredFields?.filter(
      (field) =>
        !reportDetails.some((detail) => detail.name === field.name && isNotEmpty(detail.value)),
    );

    if (missingFields?.length) {
      Object.assign(result, {
        ...result,
        [key]: {
          error: true,
          findingIndex,
          observationIndex,
          requiredConditions: missingFields.map((field) => field.name),
        },
      });
    }
  });
}

// Spine organ requires special handling: degenerative change findings must be validated
// together as a group, using the main degenerative change finding's index for all related findings.
// This ensures proper validation of interdependent condition fields.
export function findMissingFieldsInDegenerativeChangeFindings(
  organData: TextToStructuredObservation[],
  result: MissingField,
  key: string,
) {
  const degenerativeChangeIndex = organData.findIndex(
    (finding) => finding.conditionTemplate.condition === DEGENERATIVE_CHANGE_CONDITION,
  );

  const degenerativeChangeFindings = organData.filter((finding) =>
    DEGENERATIVE_CHANGE_CONDITIONS.includes(finding.conditionTemplate.condition!),
  );

  degenerativeChangeFindings.forEach((finding) => {
    evaluateMissingFields(
      finding,
      result,
      key,
      degenerativeChangeIndex,
      degenerativeChangeFindings,
    );
  });

  return organData.filter(
    (finding) => !DEGENERATIVE_CHANGE_CONDITIONS.includes(finding.conditionTemplate.condition!),
  );
}

export function findMissingFieldsInGroupedFindings(groupedData: FindingsByOrgan): MissingField {
  return Object.entries(groupedData).reduce((result, [key, organData]) => {
    let filteredOrganData = organData;

    if (key === 'spine') {
      filteredOrganData = findMissingFieldsInDegenerativeChangeFindings(organData, result, key);
    }

    filteredOrganData.forEach((finding, findingIndex) => {
      evaluateMissingFields(finding, result, key, findingIndex, filteredOrganData);
    });

    return result;
  }, {} as MissingField);
}

export const formatOrganName = (organName: string) => {
  const skeletonAndSoftTissuesOrgan = CARE_SERVICE_ORGAN_FULL_LIST.find(
    (organ) => organ.key === 'bony_skeleton',
  );

  if (skeletonAndSoftTissuesOrgan && organName === skeletonAndSoftTissuesOrgan.key) {
    return skeletonAndSoftTissuesOrgan.value;
  }

  return organName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export function formatConditionName(
  finding: TextToStructuredObservation,
  isConditionTemplateDisplayNameEnabled: boolean,
) {
  const keyImageLabel = finding.conditionTemplate.keyImageLabel ?? null;
  const trimmedCondition = trimConditionName(finding.conditionTemplate.condition!);
  const { displayName } = finding.conditionTemplate;

  if (keyImageLabel) {
    return keyImageLabel;
  }

  if (trimmedCondition === 'other') {
    return finding.renderedSummary.findingSummary !== 'None'
      ? `${finding.renderedSummary.findingSummary}`
      : getConditionTemplateName(
          { condition: trimmedCondition, displayName },
          isConditionTemplateDisplayNameEnabled,
        );
  }

  return getConditionTemplateName(
    { condition: trimmedCondition, displayName },
    isConditionTemplateDisplayNameEnabled,
  );
}

export const isConditionEmpty = (finding: TextToStructuredObservation) =>
  finding?.renderedSummary?.reportFindings?.every((reportFinding) =>
    reportFinding.conditionDetail.every((conditionDetail) => isEmpty(conditionDetail.value)),
  );
